<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import EmpresaNoteItem from './EmpresaNoteItem.vue';

const props = defineProps({
  empresaId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const draft = ref('');

const notes = computed(() =>
  store.getters['empresaNotes/getNotesByEmpresa'](Number(props.empresaId))
);
const uiFlags = computed(() => store.getters['empresaNotes/getUIFlags']);

const loadNotes = () =>
  store.dispatch('empresaNotes/get', { empresaId: props.empresaId });

const addNote = async () => {
  const content = draft.value.trim();
  if (!content || uiFlags.value.isCreating) return;
  try {
    await store.dispatch('empresaNotes/create', {
      empresaId: props.empresaId,
      content,
    });
    draft.value = '';
  } catch (error) {
    useAlert(t('EMPRESAS.NOTAS.ERROR'));
  }
};

const deleteNote = async noteId => {
  try {
    await store.dispatch('empresaNotes/delete', {
      empresaId: props.empresaId,
      noteId,
    });
  } catch (error) {
    useAlert(t('EMPRESAS.NOTAS.ERROR'));
  }
};

watch(() => props.empresaId, loadNotes);
onMounted(loadNotes);
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Composer -->
    <div class="flex flex-col gap-2">
      <textarea
        v-model="draft"
        rows="2"
        :placeholder="t('EMPRESAS.NOTAS.PLACEHOLDER')"
        class="w-full px-2.5 py-1.5 text-sm border rounded-md reset-base resize-none bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        @keydown.meta.enter="addNote"
        @keydown.ctrl.enter="addNote"
      />
      <Button
        :label="t('EMPRESAS.NOTAS.ADD')"
        icon="i-lucide-plus"
        size="sm"
        color="blue"
        class="self-end"
        :is-loading="uiFlags.isCreating"
        :disabled="!draft.trim()"
        @click="addNote"
      />
    </div>

    <!-- List -->
    <p
      v-if="!notes.length && !uiFlags.isFetching"
      class="py-1 text-sm text-n-slate-10"
    >
      {{ t('EMPRESAS.NOTAS.EMPTY') }}
    </p>
    <div v-else class="flex flex-col divide-y divide-n-weak/60">
      <EmpresaNoteItem
        v-for="note in notes"
        :key="note.id"
        :note="note"
        @delete="deleteNote"
      />
    </div>
  </div>
</template>
